import { LABEL_COLOURS, type LabelColour } from '@molt/shared';

/**
 * The product's two data-colour palettes — `FE-04.10`, and **the only file in
 * this repository allowed to contain a colour literal**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠ READ THIS BEFORE USING THE FILE: it is a PROPOSAL awaiting ratification
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Neither channel has a source anywhere in the corpus, and three independent
 * Stage 2 design returns each found that separately:
 *
 *  - `db/postgres/ENUMS.md`'s `labels.colour` row lists **31 names and no
 *    values**; `packages/shared/src/label.ts` is an enum of the same names.
 *  - `users.initials_colour` is **ten unnamed colour words** whose only
 *    definition anywhere is the `INITIALS_PALETTE` literal in
 *    `apps/server/src/auth/profile.ts` — no `ENUMS.md` row, no CHECK, no hex.
 *
 * `docs/design/stage2/COLLISIONS.md` states the consequence plainly: *"whoever
 * builds `LabelChip` writes the palette, and it becomes the palette by
 * default."* So it is written down here, once, with its arithmetic and its
 * measurements attached, rather than being scattered across `FE-15.2`,
 * `FE-19.3` and `FE-42.2` as three near-misses. **A human has to ratify or
 * reject it**, and the numbers below are what they should judge it on.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE METHOD, IN FULL
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **1. Ten families × three lightness tiers, plus a fourth treatment.**
 * `LABEL_COLOURS` is `colourless` plus ten families each spelled `_subtle`,
 * bare and `_bold`. The suffix is read as what it plainly is — a lightness
 * tier — so the palette is three decisions and ten hues, not 31 inventions.
 * That structure is `docs/design/stage2/card-back.md`'s D8; the values below
 * are not (see "what changed from D8").
 *
 * **2. Every fill is in the sRGB gamut, and this is the correction to D8.**
 * D8 proposed one chroma per tier — 0.05 / 0.12 / 0.145. Measured against the
 * sRGB boundary, those are **outside it for most of the ten hues**: at
 * L=93% the ceiling is 0.032 for blue, 0.034 for red and 0.040 for pink and
 * purple, so `_subtle` was out of gamut for six families of nine; at L=48%
 * yellow tops out at 0.098 and sky at 0.092, so `_bold` was out for five.
 * An out-of-gamut `oklch()` is not an error — the browser gamut-maps it — but
 * it means the rendered colour is not the written one, and any contrast figure
 * computed from the written one is fiction. Every fill here is therefore
 * `min(tier target, 92% of the hue's in-gamut chroma ceiling)`, which is why
 * `blue` carries 0.102 where `sky` carries 0.130. Nothing renders through a
 * gamut mapper, and the pinned ratios are the ratios that ship.
 *
 * **3. The tier lightnesses are 90% / 78% / 47%.** `_subtle` moved up from
 * D8's 93% because at 93% the chroma ceiling collapses (blue 0.032) and the
 * whole tier greys out; 90% buys back roughly 50% more chroma and a little
 * more separation from the page. `_bold` moved to 47% for the same headroom
 * reason. The middle tier is 78%.
 *
 * **4. Ink is chosen per swatch, by measurement, not once per tier.** Each
 * fill is scored against BOTH board inks and takes the winner, with the
 * measured ratio pinned beside it. There are exactly two candidate inks and
 * they are `--color-board-ink-dark` and `--color-board-ink-light` — the two
 * colours in `styles.css` that are deliberately **not** redeclared per theme,
 * because a board background is shared state and the theme is a private
 * preference. A label colour is the same category of thing, so it inherits the
 * same reasoning rather than a third fixed black. **No new ink is minted.**
 *
 * The per-swatch method is what `docs/design/stage2/entry-surfaces.md`'s C6
 * demands (*"`black` and `yellow` cannot carry the same initials colour"*) and
 * it is honoured literally in `INITIALS_SWATCH_OF` below: `yellow` takes dark
 * ink at 9.14:1 and `black` takes light ink at 6.46:1. Within the label tiers
 * the measurement happens to land uniformly — all thirty `_subtle` and base
 * swatches choose dark, all ten `_bold` choose light — and that is a *result*,
 * recorded, not an assumption baked into the shape of the file. Change a hue
 * or a lightness and `palette.test.ts` recomputes all 31 and fails.
 *
 * **5. Worst measured case: 6.10:1** (`green_bold`), against a 4.5:1 AA floor
 * for the small text a chip carries. The tiers measure 13.20–13.71 (`_subtle`),
 * 8.55–9.53 (base) and 6.10–7.02 (`_bold`).
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHAT A CHIP'S EDGE DOES NOT ENCODE, AND WHY A SWATCH'S DOES
 * ───────────────────────────────────────────────────────────────────────────
 *
 * A **chip** is display: it carries its name in text, so its colour is
 * redundant and its edge encodes nothing. It takes no border.
 *
 * A **swatch** (the label editor's palette grid, `FE-15.1` — not built here)
 * is a *control* whose fill is the only thing it says, so WCAG 2.2 **1.4.11**
 * applies to that fill. It fails, in both themes, in opposite tiers.
 * `SWATCH_FILL_VS_SURFACE` below is the measurement, kept in this file so the
 * chunk that builds the grid does not have to rediscover it: the answer is a
 * permanent 1px `--color-border-strong` edge (3.12:1 light, 3.08:1 dark
 * against `--color-surface-raised`), never a tuned fill.
 *
 * **Do not unify the two.** They look like one object and are not.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THESE COLOURS ARE NOT RECOLOURED BY THE THEME
 * ───────────────────────────────────────────────────────────────────────────
 *
 * A label colour is board data that members agreed on, and an initials colour
 * is per-account data — the same argument `styles.css` makes for a chosen board
 * background (`FR-NOTIF-030`). So the thirty fills and their inks are fixed in
 * both themes, and only their surroundings change. The two exceptions are the
 * two "no colour" values — `colourless` and the empty-string fallback — which
 * are *absences* rather than colours, and therefore resolve through theme
 * tokens like everything else.
 *
 * Colour-blind label patterns are `FE-42.2` and are not here. This file is
 * where that block will get its ten families from.
 */

// ---------------------------------------------------------------------------
// The colour arithmetic.
//
// It lives in this module rather than in the test because the pinned ratios
// below are only trustworthy if something recomputes them from the literals
// that ship. `palette.test.ts` does exactly that, for all 31, on every run.
// ---------------------------------------------------------------------------

/** WCAG 2.2 1.4.3 AA, text below 18.66px/24px — what a chip and an avatar carry. */
export const AA_TEXT = 4.5;

/** WCAG 2.2 1.4.11, non-text contrast — what a control's boundary needs. */
export const AA_NON_TEXT = 3;

/** `oklch(90% 0.05 150)` → `[0.90, 0.05, 150]`. Percent or unit L, both accepted. */
export function parseOklch(value: string): readonly [number, number, number] {
  const match = /^oklch\(\s*([\d.]+)(%?)\s+([\d.]+)\s+([\d.]+)\s*\)$/.exec(value.trim());
  if (!match) throw new Error(`not an oklch() literal: ${value}`);
  const [, l, percent, c, h] = match as unknown as [
    string,
    string,
    string,
    string,
    string,
  ];
  return [percent === '%' ? Number(l) / 100 : Number(l), Number(c), Number(h)];
}

/**
 * OKLCH → OKLab → linear sRGB. The matrices are Björn Ottosson's published
 * OKLab constants; nothing here is tuned.
 *
 * ⚠ **Public because alpha compositing needs the three channels, and
 * `relativeLuminance` has already collapsed them.** Two callers hit that wall
 * independently before it was exported — `styles.tokens.test.ts` answered it
 * with a private `composited()` helper, and `C1`'s `board-contrast.test.ts`
 * with a copied mirror plus a drift-guard test. A blend is per-channel; a
 * luminance is one number; no amount of care recovers the first from the
 * second. Exporting it is what stops the third copy.
 */
export function toLinearSrgb(
  l: number,
  c: number,
  hDeg: number,
): [number, number, number] {
  const h = (hDeg * Math.PI) / 180;
  const a = c * Math.cos(h);
  const b = c * Math.sin(h);
  const lms = [
    (l + 0.3963377774 * a + 0.2158037573 * b) ** 3,
    (l - 0.1055613458 * a - 0.0638541728 * b) ** 3,
    (l - 0.0894841775 * a - 1.291485548 * b) ** 3,
  ] as const;
  return [
    4.0767416621 * lms[0] - 3.3077115913 * lms[1] + 0.2309699292 * lms[2],
    -1.2684380046 * lms[0] + 2.6097574011 * lms[1] - 0.3413193965 * lms[2],
    -0.0041960863 * lms[0] - 0.7034186147 * lms[1] + 1.707614701 * lms[2],
  ];
}

/**
 * Is this `oklch()` inside the sRGB gamut?
 *
 * The question that corrected D8. Outside it, a browser silently gamut-maps and
 * renders something other than what is written — so a contrast figure computed
 * from the written value describes a colour nobody sees.
 */
export function isInSrgbGamut(value: string, epsilon = 1e-4): boolean {
  return toLinearSrgb(...parseOklch(value)).every(
    (v) => v >= -epsilon && v <= 1 + epsilon,
  );
}

/** WCAG 2.x relative luminance of an `oklch()` literal. */
export function relativeLuminance(value: string): number {
  const [r, g, b] = toLinearSrgb(...parseOklch(value)).map((v) =>
    Math.min(1, Math.max(0, v)),
  ) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.x contrast ratio between two `oklch()` literals, 1–21. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [
    number,
    number,
  ];
  return (hi + 0.05) / (lo + 0.05);
}

// ---------------------------------------------------------------------------
// The token values every ratio in this file was measured against.
//
// Duplicating a token value is normally the drift this repository forbids, and
// the arithmetic above cannot run without numbers. The guard is in
// `palette.test.ts`: it reads `apps/web/src/styles.css` and fails if any of
// these has moved, because a token nudge would otherwise invalidate all 31
// pinned ratios with nothing going red.
// ---------------------------------------------------------------------------

export const MEASURED_AGAINST = {
  /** `--color-board-ink-dark`. Not redeclared per theme — see the header. */
  boardInkDark: 'oklch(20% 0.02 62)',
  /** `--color-board-ink-light`. Not redeclared per theme. */
  boardInkLight: 'oklch(98% 0.006 88)',
  /** `--color-surface`, light half. */
  surfaceLight: 'oklch(98.6% 0.008 88)',
  /** `--color-surface`, dark half. */
  surfaceDark: 'oklch(24% 0.014 66)',
  /** `--color-surface-raised`, light half. */
  surfaceRaisedLight: 'oklch(96% 0.012 84)',
  /** `--color-surface-raised`, dark half. */
  surfaceRaisedDark: 'oklch(28.5% 0.016 66)',
  /** `--color-border-strong`, light half. */
  borderStrongLight: 'oklch(63% 0.016 82)',
  /** `--color-border-strong`, dark half. */
  borderStrongDark: 'oklch(56% 0.018 66)',
  /** `--color-ink`, light half. */
  themeInkLight: 'oklch(27% 0.02 62)',
  /** `--color-ink`, dark half. */
  themeInkDark: 'oklch(93% 0.012 80)',
} as const;

/** The `MEASURED_AGAINST` key each `SwatchInk` names. */
export const BOARD_INK_OF: Readonly<Record<SwatchInk, keyof typeof MEASURED_AGAINST>> = {
  dark: 'boardInkDark',
  light: 'boardInkLight',
};

// ---------------------------------------------------------------------------
// The shape of a swatch.
// ---------------------------------------------------------------------------

/** The ten families shared by `labels.colour` and `users.initials_colour`. */
export const PALETTE_FAMILIES = [
  'green',
  'yellow',
  'orange',
  'red',
  'purple',
  'blue',
  'sky',
  'lime',
  'pink',
  'black',
] as const;

export type PaletteFamily = (typeof PALETTE_FAMILIES)[number];

/** The three lightness tiers the `_subtle` / bare / `_bold` suffixes name. */
export type PaletteTier = 'subtle' | 'base' | 'bold';

/**
 * Which of the two board inks a fill carries. `dark` is
 * `--color-board-ink-dark`; `light` is `--color-board-ink-light`.
 */
export type SwatchInk = 'dark' | 'light';

export interface Swatch {
  /** An `oklch()` literal, in gamut. The one kind of value only this file holds. */
  readonly fill: string;
  /** Measured, not assumed — the better of the two board inks on this fill. */
  readonly ink: SwatchInk;
  /** WCAG 2.x contrast of `fill` against the chosen ink. Recomputed by the test. */
  readonly inkContrast: number;
  readonly family: PaletteFamily;
  readonly tier: PaletteTier;
}

/** Every `LabelColour` except `colourless`, which is a treatment rather than a colour. */
export type LabelColourWithFill = Exclude<LabelColour, 'colourless'>;

// ---------------------------------------------------------------------------
// The thirty fills.
//
// Ordered as `LABEL_COLOURS` orders them, which is `ck_labels_colour`'s own
// order, so a drift between the two lists shows up in a diff rather than only
// in a failing render. The hex in each trailing comment is the sRGB the
// `oklch()` resolves to — a reading aid for review, never read by code.
// ---------------------------------------------------------------------------

export const LABEL_SWATCHES: Readonly<Record<LabelColourWithFill, Swatch>> = {
  green_subtle: {
    fill: 'oklch(90% 0.050 150)',
    ink: 'dark',
    inkContrast: 13.71,
    family: 'green',
    tier: 'subtle',
  }, // #c8e8cd
  green: {
    fill: 'oklch(78% 0.130 150)',
    ink: 'dark',
    inkContrast: 9.53,
    family: 'green',
    tier: 'base',
  }, // #76cf8a
  green_bold: {
    fill: 'oklch(47% 0.118 150)',
    ink: 'light',
    inkContrast: 6.1,
    family: 'green',
    tier: 'bold',
  }, // #176c35

  yellow_subtle: {
    fill: 'oklch(90% 0.050 100)',
    ink: 'dark',
    inkContrast: 13.52,
    family: 'yellow',
    tier: 'subtle',
  }, // #e5dfba
  yellow: {
    fill: 'oklch(78% 0.130 100)',
    ink: 'dark',
    inkContrast: 9.14,
    family: 'yellow',
    tier: 'base',
  }, // #cbb94c
  yellow_bold: {
    fill: 'oklch(47% 0.088 100)',
    ink: 'light',
    inkContrast: 6.41,
    family: 'yellow',
    tier: 'bold',
  }, // #665b15

  orange_subtle: {
    fill: 'oklch(90% 0.050 62)',
    ink: 'dark',
    inkContrast: 13.35,
    family: 'orange',
    tier: 'subtle',
  }, // #f7d7bd
  orange: {
    fill: 'oklch(78% 0.130 62)',
    ink: 'dark',
    inkContrast: 8.81,
    family: 'orange',
    tier: 'base',
  }, // #f2a359
  orange_bold: {
    fill: 'oklch(47% 0.098 62)',
    ink: 'light',
    inkContrast: 6.63,
    family: 'orange',
    tier: 'bold',
  }, // #814c14

  red_subtle: {
    fill: 'oklch(90% 0.046 27)',
    ink: 'dark',
    inkContrast: 13.25,
    family: 'red',
    tier: 'subtle',
  }, // #fbd3ce
  red: {
    fill: 'oklch(78% 0.116 27)',
    ink: 'dark',
    inkContrast: 8.66,
    family: 'red',
    tier: 'base',
  }, // #f99a8f
  red_bold: {
    fill: 'oklch(47% 0.150 27)',
    ink: 'light',
    inkContrast: 6.96,
    family: 'red',
    tier: 'bold',
  }, // #9e2d28

  purple_subtle: {
    fill: 'oklch(90% 0.050 305)',
    ink: 'dark',
    inkContrast: 13.28,
    family: 'purple',
    tier: 'subtle',
  }, // #e5d6f9
  purple: {
    fill: 'oklch(78% 0.128 305)',
    ink: 'dark',
    inkContrast: 8.68,
    family: 'purple',
    tier: 'base',
  }, // #caa2f9
  purple_bold: {
    fill: 'oklch(47% 0.150 305)',
    ink: 'light',
    inkContrast: 6.93,
    family: 'purple',
    tier: 'bold',
  }, // #6f3e9d

  blue_subtle: {
    fill: 'oklch(90% 0.044 264)',
    ink: 'dark',
    inkContrast: 13.45,
    family: 'blue',
    tier: 'subtle',
  }, // #cfdefc
  blue: {
    fill: 'oklch(78% 0.102 264)',
    ink: 'dark',
    inkContrast: 9.02,
    family: 'blue',
    tier: 'base',
  }, // #96b7f9
  blue_bold: {
    fill: 'oklch(47% 0.150 264)',
    ink: 'light',
    inkContrast: 6.62,
    family: 'blue',
    tier: 'bold',
  }, // #2e54ae

  sky_subtle: {
    fill: 'oklch(90% 0.050 228)',
    ink: 'dark',
    inkContrast: 13.61,
    family: 'sky',
    tier: 'subtle',
  }, // #bce5f8
  sky: {
    fill: 'oklch(78% 0.130 228)',
    ink: 'dark',
    inkContrast: 9.33,
    family: 'sky',
    tier: 'base',
  }, // #47c7f8
  sky_bold: {
    fill: 'oklch(47% 0.082 228)',
    ink: 'light',
    inkContrast: 6.3,
    family: 'sky',
    tier: 'bold',
  }, // #19637f

  lime_subtle: {
    fill: 'oklch(90% 0.050 128)',
    ink: 'dark',
    inkContrast: 13.64,
    family: 'lime',
    tier: 'subtle',
  }, // #d4e5c2
  lime: {
    fill: 'oklch(78% 0.130 128)',
    ink: 'dark',
    inkContrast: 9.38,
    family: 'lime',
    tier: 'base',
  }, // #9fc769
  lime_bold: {
    fill: 'oklch(47% 0.112 128)',
    ink: 'light',
    inkContrast: 6.22,
    family: 'lime',
    tier: 'bold',
  }, // #486614

  pink_subtle: {
    fill: 'oklch(90% 0.050 348)',
    ink: 'dark',
    inkContrast: 13.2,
    family: 'pink',
    tier: 'subtle',
  }, // #f9d1e4
  pink: {
    fill: 'oklch(78% 0.130 348)',
    ink: 'dark',
    inkContrast: 8.55,
    family: 'pink',
    tier: 'base',
  }, // #f494c6
  pink_bold: {
    fill: 'oklch(47% 0.150 348)',
    ink: 'light',
    inkContrast: 7.02,
    family: 'pink',
    tier: 'bold',
  }, // #932d69

  /**
   * The `black` family carries chroma 0.006 / 0.010 / 0.014 rather than the
   * tier chroma, on hue 70 — inside Paper's own warm band. It is a warm grey
   * ramp, which is what "black" means in a label palette (Trello's is the
   * same), and `black_subtle` being a pale warm grey looks like a bug for
   * about a second until you notice the family is called black and the tier is
   * called subtle.
   */
  black_subtle: {
    fill: 'oklch(90% 0.006 70)',
    ink: 'dark',
    inkContrast: 13.45,
    family: 'black',
    tier: 'subtle',
  }, // #e1ddda
  black: {
    fill: 'oklch(78% 0.010 70)',
    ink: 'dark',
    inkContrast: 9.06,
    family: 'black',
    tier: 'base',
  }, // #bcb6b1
  black_bold: {
    fill: 'oklch(47% 0.014 70)',
    ink: 'light',
    inkContrast: 6.46,
    family: 'black',
    tier: 'bold',
  }, // #605a53
};

/**
 * `colourless` is the fourth treatment, and it is not a colour.
 *
 * `labels.colour` is `NOT NULL`, so "this label has no colour" has exactly one
 * spelling and it is this string (`packages/shared/src/label.ts`). Being an
 * absence rather than board data, it is the one label value that DOES follow
 * the theme: an opaque `--color-surface-raised` pill, `--color-ink` text, and —
 * uniquely among chips — a `--color-border-strong` edge, because without one it
 * is an invisible pill rather than a quiet one.
 *
 * Measured: ink on surface-raised is 13.46:1 light and 11.70:1 dark; the edge
 * is 3.12:1 light and 3.08:1 dark, clearing 1.4.11 in both.
 */
export const COLOURLESS_TREATMENT = {
  fill: 'var(--color-surface-raised)',
  ink: 'var(--color-ink)',
  border: 'var(--color-border-strong)',
  inkContrastLight: 13.46,
  inkContrastDark: 11.7,
  borderContrastLight: 3.12,
  borderContrastDark: 3.08,
} as const;

/**
 * Fill-against-surface, per tier, in both themes — the numbers that decide a
 * swatch grid, and the numbers that say a chip must not copy it.
 *
 * Every one of the six is below 1.4.11's 3:1 except `_bold` in light theme.
 * Read the columns: `_subtle` all but vanishes on the light page (1.27–1.32)
 * and `_bold` all but vanishes on the dark one (2.22–2.55). **A boundary that
 * survives one theme dies in the other**, which is the same asymmetry all three
 * Stage 2 returns hit independently, arriving here for the third time. There is
 * no fill that clears it in both, because the tiers are a lightness ramp and the
 * two surfaces sit at opposite ends of it — which is precisely why the fix is
 * `--color-border-strong` and not a tuned fill.
 *
 * A chip is unaffected: it carries its name in text at 6.10:1 or better, so
 * nothing is lost when the pill blends into the page.
 */
export const SWATCH_FILL_VS_SURFACE = {
  subtle: { light: [1.27, 1.32], dark: [11.98, 12.45] },
  base: { light: [1.83, 2.04], dark: [7.76, 8.65] },
  bold: { light: [6.21, 7.14], dark: [2.22, 2.55] },
} as const;

// ---------------------------------------------------------------------------
// The initials palette — ten words, ten swatches drawn from the thirty above.
// ---------------------------------------------------------------------------

/**
 * `users.initials_colour` → the label swatch an avatar paints with.
 *
 * **One palette, not two.** The ten words are the same ten families
 * (`INITIALS_PALETTE` in `apps/server/src/auth/profile.ts`, whose own comment
 * says it uses named tokens *precisely* so "the web layer already needs one
 * token → CSS map for labels and lists"). Minting a second set of ten colours
 * would make that comment false and give `FE-42.2` two palettes to pattern.
 *
 * **Which tier, and the one rule that varies it.** `card-back.md`'s D9 mapped
 * all ten to the bold tier and asked to be overruled if that was wrong. It is,
 * for two families: at 47% lightness `yellow` and `lime` are olive
 * (`#665b15`, `#486614`) and stop being the colour they are named after. Those
 * are exactly the two families whose sRGB gamut is light-biased — the same
 * measurement that caps their bold chroma at 0.088 and 0.112 — so the rule is
 * not taste: **a family whose gamut is light-biased takes the base tier;
 * every other family takes bold.**
 *
 * ⚠ **Read the causality the right way round.** `entry-surfaces.md` C6 says
 * *"`black` and `yellow` cannot carry the same initials colour"*, and this
 * palette does flip: `yellow` takes DARK ink at 9.14:1 and `black` takes LIGHT
 * ink at 6.46:1. But no contrast constraint forced that. At D9's uniform bold
 * tier `yellow_bold` takes light ink at 6.41:1 and clears AA perfectly well —
 * C6's premise is not literally true at a single tier. The flip is a
 * **consequence** of splitting the tiers for identity, not the reason for it.
 * Anyone tempted to collapse the ten back onto one tier should overrule the
 * identity argument on its own terms, not on the contrast numbers.
 *
 * An avatar therefore never carries a theme colour — and `avatarUrl`, a real
 * image, overrides all of it.
 */
export const INITIALS_SWATCH_OF: Readonly<Record<PaletteFamily, LabelColourWithFill>> = {
  green: 'green_bold',
  yellow: 'yellow', // base tier — see the rule above
  orange: 'orange_bold',
  red: 'red_bold',
  purple: 'purple_bold',
  blue: 'blue_bold',
  sky: 'sky_bold',
  lime: 'lime', // base tier — see the rule above
  pink: 'pink_bold',
  black: 'black_bold',
};

/**
 * What an avatar paints when its colour word is not one of the ten.
 *
 * `users.initials_colour` is `NOT NULL DEFAULT ''` and
 * `publicProfileSchema.initialsColour` carries no `.min(1)`, both deliberately:
 * any row not created through the sign-up hook — a fixture, a seed, a
 * hand-inserted test row — legitimately holds `''`. An Avatar that assumes a
 * non-empty swatch renders a transparent, empty circle for real data.
 *
 * The fallback is the same treatment `colourless` gets, and for the same
 * reason: this is the absence of a colour, not a colour, so it follows the
 * theme. That also makes "no colour" render identically in both data channels,
 * which is one fewer thing for a member to learn.
 */
export const NO_COLOUR_FALLBACK = COLOURLESS_TREATMENT;

// ---------------------------------------------------------------------------
// Lookups.
// ---------------------------------------------------------------------------

/**
 * The fill and ink for a label colour, or `null` for `colourless`.
 *
 * `null` is the signal to use `COLOURLESS_TREATMENT` — the caller renders a
 * different treatment, not a different colour, so a `null` here is not a
 * missing value.
 */
export function labelSwatch(colour: LabelColour): Swatch | null {
  return colour === 'colourless' ? null : (LABEL_SWATCHES[colour] ?? null);
}

/**
 * The fill and ink for an `initials_colour` word, or `null` when the column
 * holds `''` or anything else outside the ten.
 *
 * Takes a bare `string`, not a union: the column has no CHECK, so the ten words
 * are a convention the database does not enforce and narrowing the parameter
 * would be claiming a guarantee nothing provides.
 */
export function initialsSwatch(initialsColour: string): Swatch | null {
  const family = (PALETTE_FAMILIES as readonly string[]).includes(initialsColour)
    ? (initialsColour as PaletteFamily)
    : null;
  return family === null ? null : LABEL_SWATCHES[INITIALS_SWATCH_OF[family]];
}

const TIER_WORD: Readonly<Record<PaletteTier, string>> = {
  subtle: 'Subtle ',
  base: '',
  bold: 'Bold ',
};

export type LabelColourTranslate = (key: string) => string;

const LABEL_COLOUR_KEYS: Readonly<Record<LabelColour, string>> = {
  colourless: 'primitives.labelColour.colourless',
  green_subtle: 'primitives.labelColour.greenSubtle',
  green: 'primitives.labelColour.green',
  green_bold: 'primitives.labelColour.greenBold',
  yellow_subtle: 'primitives.labelColour.yellowSubtle',
  yellow: 'primitives.labelColour.yellow',
  yellow_bold: 'primitives.labelColour.yellowBold',
  orange_subtle: 'primitives.labelColour.orangeSubtle',
  orange: 'primitives.labelColour.orange',
  orange_bold: 'primitives.labelColour.orangeBold',
  red_subtle: 'primitives.labelColour.redSubtle',
  red: 'primitives.labelColour.red',
  red_bold: 'primitives.labelColour.redBold',
  purple_subtle: 'primitives.labelColour.purpleSubtle',
  purple: 'primitives.labelColour.purple',
  purple_bold: 'primitives.labelColour.purpleBold',
  blue_subtle: 'primitives.labelColour.blueSubtle',
  blue: 'primitives.labelColour.blue',
  blue_bold: 'primitives.labelColour.blueBold',
  sky_subtle: 'primitives.labelColour.skySubtle',
  sky: 'primitives.labelColour.sky',
  sky_bold: 'primitives.labelColour.skyBold',
  lime_subtle: 'primitives.labelColour.limeSubtle',
  lime: 'primitives.labelColour.lime',
  lime_bold: 'primitives.labelColour.limeBold',
  pink_subtle: 'primitives.labelColour.pinkSubtle',
  pink: 'primitives.labelColour.pink',
  pink_bold: 'primitives.labelColour.pinkBold',
  black_subtle: 'primitives.labelColour.blackSubtle',
  black: 'primitives.labelColour.black',
  black_bold: 'primitives.labelColour.blackBold',
};

export function labelColourMessageKey(colour: LabelColour): string {
  return LABEL_COLOUR_KEYS[colour];
}

export function labelColourDisplayName(
  t: LabelColourTranslate,
  colour: LabelColour,
): string {
  return t(labelColourMessageKey(colour));
}

/**
 * The colour's name in words — `'green_subtle'` → `'Subtle green'`.
 *
 * This is not decoration. `labelSchema.name` is nullable and a new board seeds
 * six **unnamed** coloured labels (`DEFAULT_BOARD_LABEL_COLOURS`), so a chip
 * with no name still has to have an accessible one, and the only fact about it
 * that exists is its colour (`card-back.md` D10). British spelling throughout,
 * per the repository's writing convention — hence `Colourless`.
 *
 * Source-English fallback only. Components with catalogue access should call
 * `labelColourDisplayName(t, colour)` so the fixed colour words can translate.
 */
export function labelColourName(colour: LabelColour): string {
  if (colour === 'colourless') return 'Colourless';
  const swatch = LABEL_SWATCHES[colour];
  if (!swatch) return colour;
  const word = `${TIER_WORD[swatch.tier]}${swatch.family}`;
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * Re-exported so a caller iterating the palette does not need two imports, and
 * so `palette.test.ts` can assert this file covers `ck_labels_colour`'s live
 * member list exactly — no swatch missing, none invented.
 */
export { LABEL_COLOURS };
export type { LabelColour };
